import {
  Box,
  Group,
  Stack,
  Text,
  useMantineTheme,
} from '@mantine/core';
import { useState, forwardRef } from 'react';
import { IconCheck } from '@tabler/icons-react';
import { Button, Paper, Stepper } from '@/components/ui';
import { usePreferencesStore } from '../../../stores';
import { LINE_HEIGHT_MAP, LETTER_SPACING_MAP } from '../../../hooks/useAccessibilityStyles';

export interface FormStep {
  title: string;
  description?: string;
  icon?: React.ReactNode;
  content: React.ReactNode;
  validate?: () => boolean | Promise<boolean>;
  onEnter?: () => void;
  onLeave?: () => void;
}

export interface MultiStepFormProps {
  steps: FormStep[];
  onComplete: () => void | Promise<void>;
  onCancel?: () => void;
  completeLabel?: string;
  nextLabel?: string;
  prevLabel?: string;
  cancelLabel?: string;
  loading?: boolean;
  gradientVariant?: 'primary' | 'secondary' | 'tertiary';
  allowSkip?: boolean;
  showStepNumbers?: boolean;
  orientation?: 'horizontal' | 'vertical';
  completedContent?: React.ReactNode;
}

const MultiStepForm = forwardRef<HTMLDivElement, MultiStepFormProps>(
  (
    {
      steps,
      onComplete,
      onCancel,
      completeLabel = 'Complete',
      nextLabel = 'Next',
      prevLabel = 'Back',
      cancelLabel = 'Cancel',
      loading = false,
      gradientVariant = 'primary',
      allowSkip = false,
      showStepNumbers = true,
      orientation = 'horizontal',
      completedContent,
    },
    ref
  ) => {
    const theme = useMantineTheme();
    const gradient = theme.other?.gradients?.[gradientVariant];
    const lineSpacing = usePreferencesStore((state) => state.lineSpacing);
    const letterSpacingPref = usePreferencesStore((state) => state.letterSpacing);

    const accessibilityStyles = {
      lineHeight: LINE_HEIGHT_MAP[lineSpacing],
      letterSpacing: LETTER_SPACING_MAP[letterSpacingPref],
    };

    const [activeStep, setActiveStep] = useState(0);
    const [completed, setCompleted] = useState(false);
    const [validating, setValidating] = useState(false);

    const currentStep = steps[activeStep];
    const isLastStep = activeStep === steps.length - 1;
    const isFirstStep = activeStep === 0;

    const handleNext = async () => {
      if (currentStep.validate) {
        setValidating(true);
        const isValid = await currentStep.validate();
        setValidating(false);
        if (!isValid) return;
      }

      currentStep.onLeave?.();

      if (isLastStep) {
        await onComplete();
        setCompleted(true);
      } else {
        const nextStep = steps[activeStep + 1];
        nextStep.onEnter?.();
        setActiveStep((prev) => prev + 1);
      }
    };

    const handlePrev = () => {
      if (!isFirstStep) {
        currentStep.onLeave?.();
        const prevStep = steps[activeStep - 1];
        prevStep.onEnter?.();
        setActiveStep((prev) => prev - 1);
      }
    };

    const handleStepClick = (step: number) => {
      if (allowSkip || step < activeStep) {
        currentStep.onLeave?.();
        steps[step].onEnter?.();
        setActiveStep(step);
      }
    };

    if (completed && completedContent) {
      return (
        <Paper ref={ref} p="xl">
          {completedContent}
        </Paper>
      );
    }

    return (
      <Box ref={ref}>
        <Stepper
          active={activeStep}
          onStepClick={allowSkip ? handleStepClick : undefined}
          gradientVariant={gradientVariant}
          orientation={orientation}
          mb="xl"
        >
          {steps.map((step, index) => (
            <Stepper.Step
              key={index}
              label={step.title}
              description={step.description}
              icon={step.icon}
              completedIcon={<IconCheck size={18} />}
            />
          ))}
        </Stepper>

        <Paper variant="subtle" p="lg" mb="xl">
          {currentStep.content}
        </Paper>

        <Group justify="space-between">
          <Group>
            {onCancel && (
              <Button variant="subtle" onClick={onCancel} disabled={loading}>
                {cancelLabel}
              </Button>
            )}
          </Group>

          <Group>
            {!isFirstStep && (
              <Button
                variant="outline"
                onClick={handlePrev}
                disabled={loading || validating}
              >
                {prevLabel}
              </Button>
            )}
            <Button
              onClick={handleNext}
              loading={loading || validating}
              gradientVariant={gradientVariant}
            >
              {isLastStep ? completeLabel : nextLabel}
            </Button>
          </Group>
        </Group>

        {showStepNumbers && (
          <Text ta="center" size="sm" c="dimmed" mt="md" style={accessibilityStyles}>
            Step {activeStep + 1} of {steps.length}
          </Text>
        )}
      </Box>
    );
  }
);

MultiStepForm.displayName = 'MultiStepForm';

export default MultiStepForm;
